/**
 * Orbiee Chat WebSocket client.
 *
 * Wire protocol mirror of the backend
 * (`Orbiee_Backend/src/orbiee/api/ws/protocol.py`):
 *
 *   Client → Server:
 *     { type: "start", message: string, mode?: "text"|"avatar", screen_context?: object }
 *     { type: "voice", audio_base64: string, mode?: "voice"|"avatar", screen_context?: object }
 *     { type: "stop",  reason?: string }
 *     { type: "ping" }
 *     { type: "ui_event", seq: number, ts: string, event: string, data: object }
 *
 *   Server → Client:
 *     { type: "system",       status: "connected", user_id, device_id, history_length }
 *     { type: "pong" }
 *     { type: "transcript",   data: { text, language_code, audio_duration_secs?, input_mode? } }
 *     { type: "message",      data: { source: "user"|"assistant", content, timestamp } }
 *     { type: "llm_start"     | "llm_response" | "tool_call" | "tool_result", data }
 *     { type: "completion",   status: "complete"|"stopped"|"error"|"idle",
 *                              reason, llm_calls, tool_calls }
 *     { type: "error",        error }
 *     { type: "audio_start",  data: { stream_id, kind, encoding, sample_rate, ... } }
 *     <binary frame>          4-byte big-endian stream_id + mulaw audio bytes
 *     { type: "audio_end",    data: { stream_id, bytes, reason } }
 */

import { orbieeChatWsUrl } from "../../config/apiConfig";
import { hasWsAuthToken, withAuthToken, requireWsAuth } from "./wsAuth";

export const SocketStatus = Object.freeze({
  IDLE: "idle",
  CONNECTING: "connecting",
  OPEN: "open",
  CLOSING: "closing",
  CLOSED: "closed",
  RECONNECTING: "reconnecting",
  ERROR: "error",
});

export const ServerMessageType = Object.freeze({
  SYSTEM: "system",
  PONG: "pong",
  TRANSCRIPT: "transcript",
  MESSAGE: "message",
  LLM_START: "llm_start",
  LLM_RESPONSE: "llm_response",
  TOOL_CALL: "tool_call",
  TOOL_RESULT: "tool_result",
  COMPLETION: "completion",
  ERROR: "error",
  AUDIO_START: "audio_start",
  AUDIO_END: "audio_end",
});

const AGENT_EVENT_TYPES = new Set([
  ServerMessageType.LLM_START,
  ServerMessageType.LLM_RESPONSE,
  ServerMessageType.TOOL_CALL,
  ServerMessageType.TOOL_RESULT,
]);

const DEFAULTS = {
  heartbeatIntervalMs: 25_000,
  autoReconnect: true,
  // Keep retrying for the whole session; delay is capped below.
  maxReconnectAttempts: 100,
  reconnectBaseDelayMs: 250,
  reconnectMaxDelayMs: 15_000,
  // Auth / protocol rejects only. Unexpected closes (including 1000)
  // must reconnect so one session stays on a live socket.
  noReconnectCloseCodes: [4000, 4001],
};

class Emitter {
  constructor() {
    this._handlers = new Map();
  }

  on(event, handler) {
    if (typeof handler !== "function") return () => {};
    const list = this._handlers.get(event) || [];
    list.push(handler);
    this._handlers.set(event, list);
    return () => this.off(event, handler);
  }

  off(event, handler) {
    const list = this._handlers.get(event);
    if (!list) return;
    const next = list.filter((h) => h !== handler);
    if (next.length) this._handlers.set(event, next);
    else this._handlers.delete(event);
  }

  emit(event, ...args) {
    const list = this._handlers.get(event);
    if (!list) return;
    [...list].forEach((handler) => {
      try {
        handler(...args);
      } catch (err) {
        console.error(`[OrbieeChatSocket] listener for "${event}" threw`, err);
      }
    });
  }

  removeAll() {
    this._handlers.clear();
  }
}

export class OrbieeChatSocket extends Emitter {
  constructor(opts = {}) {
    super();
    if (!opts.userId) throw new Error("OrbieeChatSocket: userId is required");
    if (!opts.deviceId) {
      throw new Error("OrbieeChatSocket: deviceId is required");
    }

    this.userId = String(opts.userId);
    this.deviceId = String(opts.deviceId);
    this.queryParams = opts.queryParams || null;

    this._baseUrl = opts.url || orbieeChatWsUrl(this.userId, this.deviceId);

    this._cfg = {
      autoReconnect: opts.autoReconnect ?? DEFAULTS.autoReconnect,
      heartbeatIntervalMs:
        opts.heartbeatIntervalMs ?? DEFAULTS.heartbeatIntervalMs,
      maxReconnectAttempts:
        opts.maxReconnectAttempts ?? DEFAULTS.maxReconnectAttempts,
      reconnectBaseDelayMs:
        opts.reconnectBaseDelayMs ?? DEFAULTS.reconnectBaseDelayMs,
      reconnectMaxDelayMs:
        opts.reconnectMaxDelayMs ?? DEFAULTS.reconnectMaxDelayMs,
      noReconnectCloseCodes:
        opts.noReconnectCloseCodes ?? DEFAULTS.noReconnectCloseCodes,
    };

    this._ws = null;
    this._status = SocketStatus.IDLE;
    this._reconnectAttempts = 0;
    this._reconnectTimer = null;
    this._heartbeatTimer = null;
    this._manualClose = false;
    this._outboundQueue = [];
    this._streaming = false;
  }

  get status() {
    return this._status;
  }

  get isConnected() {
    return this._status === SocketStatus.OPEN;
  }

  get isStreaming() {
    return this._streaming;
  }

  get url() {
    let full = this._baseUrl;
    if (this.queryParams) {
      const usp = new URLSearchParams(this.queryParams).toString();
      if (usp) full = `${full}&${usp}`;
    }
    return withAuthToken(full);
  }

  get isConnecting() {
    return this._status === SocketStatus.CONNECTING;
  }

  get isReconnecting() {
    return this._status === SocketStatus.RECONNECTING;
  }

  /**
   * Open the socket if it is not already OPEN / CONNECTING.
   * Safe to call on every turn — never creates a second live connection.
   * While a backoff timer is pending, this cancels it and reconnects now.
   */
  connect() {
    this._connect(false);
  }

  _connect(fromReconnect) {
    if (this._hasLiveSocket()) return;

    if (!hasWsAuthToken()) {
      this._setStatus(SocketStatus.CLOSED);
      requireWsAuth();
      return;
    }

    this._manualClose = false;
    if (!fromReconnect) {
      this._reconnectAttempts = 0;
      this._clearReconnectTimer();
    }

    this._detachSocket();
    this._setStatus(SocketStatus.CONNECTING);

    let ws;
    try {
      ws = new WebSocket(this.url);
    } catch (err) {
      this._setStatus(SocketStatus.ERROR);
      this.emit("error", err);
      this._scheduleReconnect();
      return;
    }

    // Streamed voice audio arrives as binary frames. Without this they are
    // delivered as Blobs, whose async read reorders the chunks.
    try {
      ws.binaryType = "arraybuffer";
    } catch {
      /* non-conforming implementation */
    }

    this._ws = ws;
    ws.onopen = (ev) => this._onOpen(ev);
    ws.onmessage = (ev) => this._onMessage(ev);
    ws.onerror = (ev) => this._onError(ev);
    ws.onclose = (ev) => this._onClose(ev);
  }

  /**
   * @param {string} text
   * @param {object|null} [screenContext] - Optional compact screen snapshot
   *   (Frontend ↔ Agent sync contract) attached as `screen_context`.
   * @param {string|null} [mode] - Interaction mode: "text" | "voice" | "avatar"
   */
  sendMessage(text, screenContext = null, mode = null) {
    if (!hasWsAuthToken()) {
      requireWsAuth();
      return false;
    }
    const trimmed = (text ?? "").toString().trim();
    if (!trimmed) return false;
    if (this._streaming) return false;

    const frame = { type: "start", message: trimmed };
    if (mode === "text" || mode === "voice" || mode === "avatar") {
      frame.mode = mode;
    }
    if (screenContext && typeof screenContext === "object") {
      frame.screen_context = screenContext;
    }
    const sent = this._send(frame);
    if (sent) {
      this._streaming = true;
      this.emit("streaming-change", true);
    }
    return sent;
  }

  /**
   * Send recorded audio for server-side STT, then agent turn (same stream as `start`).
   * @param {string} audioBase64 - Base64-encoded audio (no data: URL prefix)
   * @param {object|null} [screenContext] - Optional compact screen snapshot
   *   (Frontend ↔ Agent sync contract) attached as `screen_context`.
   * @param {string|null} [mode] - Interaction mode: "voice" | "avatar" (keeps type:"voice")
   */
  sendVoice(audioBase64, screenContext = null, mode = null) {
    if (!hasWsAuthToken()) {
      requireWsAuth();
      return false;
    }
    const encoded = (audioBase64 ?? "").toString().trim();
    if (!encoded) return false;
    if (this._streaming) return false;

    const frame = { type: "voice", audio_base64: encoded };
    if (mode === "text" || mode === "voice" || mode === "avatar") {
      frame.mode = mode;
    }
    if (screenContext && typeof screenContext === "object") {
      frame.screen_context = screenContext;
    }
    const sent = this._send(frame);
    if (sent) {
      this._streaming = true;
      this.emit("streaming-change", true);
    }
    return sent;
  }

  /**
   * Fire-and-forget UI context event (Frontend ↔ Agent sync contract).
   * Sent only while the socket is OPEN — never queued, because `seq` is
   * per-connection and the `screen_context` snapshot on the next utterance
   * is the documented resync path after a reconnect.
   */
  sendUiEvent(seq, ts, event, data) {
    if (this._status !== SocketStatus.OPEN) return false;
    return this._send({ type: "ui_event", seq, ts, event, data });
  }

  stop(reason) {
    return this._send({
      type: "stop",
      reason: reason || "User requested stop",
    });
  }

  ping() {
    return this._send({ type: "ping" });
  }

  disconnect(code = 1000, reason = "Client disconnect") {
    this._manualClose = true;
    this._clearReconnectTimer();
    this._stopHeartbeat();

    if (this._ws) {
      this._setStatus(SocketStatus.CLOSING);
      try {
        this._ws.close(code, reason);
      } catch {
        /* onclose still fires */
      }
    } else {
      this._setStatus(SocketStatus.CLOSED);
    }
  }

  destroy() {
    this.disconnect();
    this.removeAll();
  }

  _onOpen() {
    this._reconnectAttempts = 0;
    this._setStatus(SocketStatus.OPEN);
    this._startHeartbeat();
    this._flushQueue();
    this.emit("open");
  }

  _onError(event) {
    this.emit("error", event);
  }

  _onClose(event) {
    this._stopHeartbeat();
    const wasStreaming = this._streaming;
    this._streaming = false;
    if (wasStreaming) this.emit("streaming-change", false);

    const closeInfo = {
      code: event?.code,
      reason: event?.reason,
      wasClean: event?.wasClean,
      wasStreaming,
    };
    console.debug("[OrbieeChatSocket] connection closed", closeInfo);
    this.emit("close", closeInfo);

    this._ws = null;

    if (this._manualClose) {
      this._setStatus(SocketStatus.CLOSED);
      return;
    }

    if (this._cfg.noReconnectCloseCodes.includes(closeInfo.code)) {
      this._setStatus(SocketStatus.CLOSED);
      return;
    }

    if (!this._cfg.autoReconnect) {
      this._setStatus(SocketStatus.CLOSED);
      return;
    }

    this._scheduleReconnect();
  }

  _onMessage(event) {
    // Streamed voice audio: binary frames, everything else is JSON text.
    if (event.data instanceof ArrayBuffer) {
      this.emit("audio-chunk", event.data);
      return;
    }

    let payload;
    try {
      payload = JSON.parse(event.data);
    } catch (err) {
      this.emit("error", new Error(`Invalid JSON from server: ${err.message}`));
      return;
    }

    this.emit("frame", payload);

    if (process.env.NODE_ENV === "development") {
      console.debug("[OrbieeChatSocket] frame", payload?.type, payload?.status ?? "");
    }

    const { type } = payload;

    switch (type) {
      case ServerMessageType.SYSTEM:
        this.emit("system", payload);
        break;

      case ServerMessageType.PONG:
        this.emit("pong", payload);
        break;

      case ServerMessageType.TRANSCRIPT:
        this.emit("transcript", payload.data || {}, payload);
        break;

      case ServerMessageType.MESSAGE:
        this.emit("message", payload.data || {}, payload);
        break;

      // Voice reply audio is additive: it never touches `_streaming`, the
      // turn lifecycle, or completion. Playback outlives the turn.
      case ServerMessageType.AUDIO_START:
        this.emit("audio-start", payload.data || {}, payload);
        break;

      case ServerMessageType.AUDIO_END:
        this.emit("audio-end", payload.data || {}, payload);
        break;

      case ServerMessageType.COMPLETION:
        if (this._streaming) {
          this._streaming = false;
          this.emit("streaming-change", false);
        }
        this.emit("completion", payload);
        break;

      case ServerMessageType.ERROR:
        // Agent stream errors use `{ type, data: { error } }`; protocol
        // errors use `{ type, error: string }`.
        // Always end the in-flight turn so the next message/voice can send —
        // empty-transcript and similar errors previously left `_streaming`
        // stuck true and blocked all later chat.
        if (this._streaming) {
          this._streaming = false;
          this.emit("streaming-change", false);
        }
        if (
          payload.data != null &&
          typeof payload.data === "object" &&
          Object.prototype.hasOwnProperty.call(payload.data, "error")
        ) {
          this.emit("agent-error", payload);
        } else {
          this.emit("server-error", payload);
        }
        break;

      default:
        if (AGENT_EVENT_TYPES.has(type)) {
          this.emit("agent-event", payload);
          this.emit(type, payload);
        } else {
          if (this._streaming) {
            console.debug(
              "[OrbieeChatSocket] unknown frame during active turn",
              payload
            );
          }
          this.emit("unknown", payload);
        }
        break;
    }
  }

  _send(frame) {
    const text = JSON.stringify(frame);
    if (this._ws && this._status === SocketStatus.OPEN) {
      try {
        this._ws.send(text);
        return true;
      } catch (err) {
        this.emit("error", err);
        return false;
      }
    }

    if (frame.type === "ping") return false;
    if (
      this._status === SocketStatus.CONNECTING ||
      this._status === SocketStatus.RECONNECTING ||
      this._status === SocketStatus.IDLE
    ) {
      this._outboundQueue.push(text);
      return true;
    }
    return false;
  }

  _flushQueue() {
    if (!this._outboundQueue.length || !this._ws) return;
    const queue = this._outboundQueue;
    this._outboundQueue = [];
    for (const text of queue) {
      try {
        this._ws.send(text);
      } catch (err) {
        this.emit("error", err);
      }
    }
  }

  _startHeartbeat() {
    this._stopHeartbeat();
    if (!this._cfg.heartbeatIntervalMs) return;
    this._heartbeatTimer = setInterval(() => {
      this.ping();
    }, this._cfg.heartbeatIntervalMs);
  }

  _stopHeartbeat() {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
  }

  _hasLiveSocket() {
    if (!this._ws) return false;
    const state = this._ws.readyState;
    return (
      (this._status === SocketStatus.OPEN ||
        this._status === SocketStatus.CONNECTING) &&
      (state === WebSocket.OPEN || state === WebSocket.CONNECTING)
    );
  }

  /**
   * Drop the native WebSocket without firing our close/reconnect handlers.
   * Used when replacing a stale handle before a new connect().
   */
  _detachSocket() {
    const ws = this._ws;
    if (!ws) return;
    this._ws = null;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try {
      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        ws.close(1000, "Replaced by new connection");
      }
    } catch {
      /* ignore */
    }
  }

  _scheduleReconnect() {
    if (this._manualClose) return;
    if (this._reconnectAttempts >= this._cfg.maxReconnectAttempts) {
      this._setStatus(SocketStatus.CLOSED);
      this.emit("reconnect-failed", {
        attempts: this._reconnectAttempts,
      });
      return;
    }

    this._reconnectAttempts += 1;
    // First retry is immediate; later retries back off so a down server
    // is not hammered, while a dropped session recovers at once.
    const delay =
      this._reconnectAttempts <= 1
        ? 0
        : Math.min(
            this._cfg.reconnectBaseDelayMs *
              2 ** (this._reconnectAttempts - 2),
            this._cfg.reconnectMaxDelayMs
          );

    this._setStatus(SocketStatus.RECONNECTING);
    this.emit("reconnecting", {
      attempt: this._reconnectAttempts,
      delayMs: delay,
    });

    this._clearReconnectTimer();
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this._connect(true);
    }, delay);
  }

  _clearReconnectTimer() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }

  _setStatus(next) {
    if (this._status === next) return;
    const prev = this._status;
    this._status = next;
    this.emit("status", next, prev);
  }
}

export default OrbieeChatSocket;
